import type { Customer } from '../types/crm';
import { phoneDigitsOnly } from './customerSearch';
import { supabaseCustomerStorage } from '../services/supabaseStorage';

export type IndividualIdentityDraft = {
  firstName: string;
  surname: string;
  emailAddress: string;
  contactPhoneNumber: string;
};

export function individualIdentityDraftFromCustomer(customer: Customer): IndividualIdentityDraft {
  return {
    firstName: String(customer.firstName || '').trim(),
    surname: String(customer.surname || '').trim(),
    emailAddress: String(customer.emailAddress || '').trim(),
    contactPhoneNumber: String(customer.contactPhoneNumber || customer.mobilePhone || '').trim(),
  };
}

export function individualIdentityDisplayName(draft: IndividualIdentityDraft): string {
  return `${draft.firstName} ${draft.surname}`.trim() || 'Unnamed';
}

export function individualCustomerMatchLabel(c: Customer): string {
  const name = `${c.firstName || ''} ${c.surname || ''}`.trim() || c.companyName || 'Unnamed';
  const email = String(c.emailAddress || '').trim();
  const phone = String(c.contactPhoneNumber || c.mobilePhone || '').trim();
  const bits = [name];
  if (email) bits.push(email);
  if (phone) bits.push(phone);
  return bits.join(' — ');
}

/** Name + email OR name + phone (≥8 digits). Returns all matching individuals. */
export function findIndividualIdentityMatches(
  candidates: Customer[],
  draft: IndividualIdentityDraft
): Customer[] {
  const firstName = draft.firstName.trim().toLowerCase();
  const surname = draft.surname.trim().toLowerCase();
  if (!firstName || !surname) return [];

  const emailLower = draft.emailAddress.trim().toLowerCase();
  const phoneDigits = phoneDigitsOnly(draft.contactPhoneNumber);

  return candidates.filter((c) => {
    if (c.customerType === 'company') return false;
    const candidateFirst = String(c.firstName || '').trim().toLowerCase();
    const candidateSurname = String(c.surname || '').trim().toLowerCase();
    if (candidateFirst !== firstName || candidateSurname !== surname) return false;

    const candidateEmail = String(c.emailAddress || '').trim().toLowerCase();
    const candidatePhone = phoneDigitsOnly(String(c.contactPhoneNumber || c.mobilePhone || ''));

    if (emailLower && candidateEmail === emailLower) return true;
    if (phoneDigits.length >= 8 && candidatePhone === phoneDigits) return true;
    return false;
  });
}

/**
 * Refresh CRM by name, then return identity matches (deduped by id).
 * Used before Create new client so matches aren't limited to in-memory roster customers.
 */
export async function searchIndividualIdentityMatches(
  draft: IndividualIdentityDraft,
  knownCustomers: Customer[] = []
): Promise<Customer[]> {
  const q = `${draft.firstName} ${draft.surname}`.trim();
  let fromDb: Customer[] = [];
  if (q) {
    try {
      fromDb = (await supabaseCustomerStorage.getCustomersList(40, 0, q, 'individual')) || [];
    } catch (e) {
      console.warn('[individualCustomerMatch] search failed', e);
    }
  }
  const byId = new Map<string, Customer>();
  for (const c of [...knownCustomers, ...fromDb]) {
    if (c?.id) byId.set(c.id, c);
  }
  return findIndividualIdentityMatches([...byId.values()], draft);
}
